import React, { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FieldLabel from 'components/shared/form/FieldLabel';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminClientsQueryKeys, adminUpdateClient } from 'services/api/admin/clients';
import { IntegrationConfig, IntegrationType } from 'services/api/fields/admin/client';
import { useToastActions } from 'stores/toastStore';

type IntegrationOption = { id: IntegrationType; name: string };

const INTEGRATION_TYPE_OPTIONS: IntegrationOption[] = [
  { id: 'matrix', name: 'MatrixCare' },
  { id: 'pcc', name: 'PointClickCare' },
  { id: 'epic', name: 'Epic' },
  { id: 'kno2', name: 'Kno2' },
];

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

type Props = {
  client: { id: string; name: string; integrationConfig?: IntegrationConfig };
  onCancel: () => void;
};

export default function ConfigureApiUserModal({ client, onCancel }: Props) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);

  const initialTypes = client.integrationConfig?.type ?? [];
  const [selectedTypes, setSelectedTypes] = useState<IntegrationOption[]>(
    INTEGRATION_TYPE_OPTIONS.filter((opt) => initialTypes.includes(opt.id))
  );
  const [pccOrgId, setPccOrgId] = useState<string>(client.integrationConfig?.pccOrgId ?? '');
  const [epicApiEndpoint, setEpicApiEndpoint] = useState<string>(client.integrationConfig?.epicApiEndpoint ?? '');

  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const includesPcc = selectedTypes.some((t) => t.id === 'pcc');
  const includesEpic = selectedTypes.some((t) => t.id === 'epic');
  const pccMissing = includesPcc && pccOrgId.trim() === '';
  const epicMissing = includesEpic && epicApiEndpoint.trim() === '';

  const mutation = useMutation({
    mutationFn: () => {
      const integrationConfig: IntegrationConfig = { type: selectedTypes.map((t) => t.id) };
      if (includesPcc) integrationConfig.pccOrgId = pccOrgId.trim();
      if (includesEpic) integrationConfig.epicApiEndpoint = epicApiEndpoint.trim();
      return adminUpdateClient(client.id, { integrationConfig });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.index() });
      addToast({ text: 'API user configuration updated. Provisioning will run in the background.' });
      onCancel();
    },
    onError: () => {
      addToast({ text: 'There was an error updating the API user configuration' });
    },
  });

  return (
    <StandardModal title='Configure API User' onCancel={onCancel}>
      <StandardModal.ModalContent>
        <p style={{ margin: '0 0 16px', fontWeight: 500 }}>{client.name}</p>
        <div style={{ textAlign: 'left' }}>
          <InputGroup>
            <MultiSelectChips
              title='Integration Types'
              options={INTEGRATION_TYPE_OPTIONS}
              values={selectedTypes}
              onChange={setSelectedTypes}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select integration types...'
              menuPortalTarget={document.body}
              disabled={!canEdit}
            />
          </InputGroup>
          {includesPcc && (
            <InputGroup>
              <FieldLabel>PCC Org ID</FieldLabel>
              <Input
                value={pccOrgId}
                onChange={(e) => setPccOrgId(e.target.value)}
                placeholder='PCC organization UUID'
                $hasError={canEdit && pccMissing}
                disabled={!canEdit}
              />
            </InputGroup>
          )}
          {includesEpic && (
            <InputGroup>
              <FieldLabel>Epic API Endpoint</FieldLabel>
              <Input
                value={epicApiEndpoint}
                onChange={(e) => setEpicApiEndpoint(e.target.value)}
                placeholder='https://fhir.example.org/interconnect-...'
                $hasError={canEdit && epicMissing}
                disabled={!canEdit}
              />
            </InputGroup>
          )}
          {canEdit && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--black50)' }}>
              Saving will re-provision Auth0 and EMR connections for this client.
            </p>
          )}
        </div>
      </StandardModal.ModalContent>
      <StandardModal.Actions>
        <ButtonGroup>
          <Button variant='ghost' onClick={onCancel}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || pccMissing || epicMissing}>
              Save
            </Button>
          )}
        </ButtonGroup>
      </StandardModal.Actions>
    </StandardModal>
  );
}
